import { Loader, ProviderConsumer, Text, ThemePrepared } from '@fluentui/react-northstar';
import { Communication } from '@fluentui/react-teams';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from "react-helmet";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import OfferingSvg from '../../svg/service-offering.svg';
import { MobileSettingsView } from '../automation/AutomationTable';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import { RenderIcon } from '../shared/components/EditIconProperty/EditIconProperty';
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import { platformService } from '../shared/services/platform.service';
import { CONTACT_ADMIN, DEFAULT_API_STATUS, DONE, ERROR, LOADING, PRODUCT_LICENSE, SOMETHING_WENT_WRONG } from '../shared/utils/constants';
import { Offering } from './Offering';
import { DraggableList, DraggableListTextContentProps } from '../shared/components/table/DraggableList';

export const OfferingList = () => {
  const { t } = useTranslation();
  const api = new platformService();
  const [apiStatus, setAPIStatus] = useState<APIStatus>(DEFAULT_API_STATUS as APIStatus);
  const [isLicensedUser, setIsLicensedUser] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [queryId, setQueryId] = useState(0);
  const [offeringList, setOfferingList] = useState<any[]>([]);
  const [search, setSearch] = useState<string>('');
  const archiveStorageKey = 'isArchivedServiceOfferingView';
  const [showArchived, setShowArchived] = useState<boolean>(JSON.parse(localStorage.getItem(archiveStorageKey) || 'false'))

  const renderRecord = (key: string) => (item) => {
    switch (key) {
      case 'name':
        return <div onClick={() => { if (isLicensedUser) { setQueryId(item.Id); setOpenEdit(true); } }} className={`${isLicensedUser ? 'cursor-pointer' : 'cursor-default'}`}>
          <Text content={item.Name} color={`${isLicensedUser && 'brand'}`} />
          {item.IsArchived && (<Text content={` ${t('common.tree-listing-constants.archived')}`} size="smaller" weight="light" />)}
        </div>
      case 'icon':
        return <RenderIcon icon={item.Icon} defaultIcon={<OfferingSvg width={20} height={20} fill="#6264A7" />} size="20px" padding="5px" />;
      case 'description':
        return <Text content={item.Description} />;
      case 'createddate':
        return <Text content={DateTime.fromJSDate(new Date(item.CreatedDate)).toLocaleString(DateTime.DATETIME_SHORT)} />;
    }
    return <></>;
  };
  
  const columns: IColumn[] = [
    { key: 'name', name: t('service-offering.fields.name'), minWidth: 150, maxWidth: 300, isMultiline: false, onRender: renderRecord('name'), isResizable: true, isCollapsible: false },
    { key: 'icon', name: t('service-offering.fields.icon'), minWidth: 75, maxWidth: 75, isMultiline: false, onRender: renderRecord('icon'), isResizable: false, isCollapsible: false },
    { key: 'description', name: t('service-offering.fields.description'), minWidth: 300, isMultiline: false, onRender: renderRecord('description'), isResizable: true, isCollapsible: false },
    { key: 'createddate', name: t('service-offering.fields.created-date'), minWidth: 150, maxWidth: 225, isMultiline: false, onRender: renderRecord('createddate'), isResizable: true, isCollapsible: false },
  ];

  const fetchData = useCallback(async () => {
    setAPIStatus(prev => ({ ...prev, status: LOADING, msg: t('service-offering.get-all-items') }));
    try {
      const { data: { value } } = await api.getServiceOfferings(`?$orderby=Order asc&v=${new Date().getTime()}${(showArchived ? '' : '&$filter=IsArchived eq false')}`);
      setOfferingList(value);

      setIsLicensedUser(await getCachedFeature(PRODUCT_LICENSE.AnalystAccess));
      setAPIStatus(prev => ({ ...prev, status: DONE }));
    } catch (err: any) {
      setAPIStatus(prev => ({ ...prev, status: ERROR, errCode: err.response.status }));
    }
  }, [showArchived]);

  
  const reorderItems = useCallback(async (items) => {
    try {
      await api.orderOfferings(items.map(x => x.Id));
      toast.success(t('service-offering.changes-saved'));
    } catch (err: any) {
      toast.error(t('service-offering.error-saving-changes'));
    }
  }, []);

  const deleteOfferingApi = (id) => api.deleteOffering(id);
  const archiveOfferingApi = (item) => api.updateOffering(item);

  useEffect(() => { fetchData(); }, [showArchived]);

  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: t('common.settings'),
        link: '/settings'
      },
      {
        title: t('service-offering.menu-title'),
        link: ''
      }
    ]
  };

  const textContents: DraggableListTextContentProps = {
    pageTitle: t('service-offering.title'),
    deleteItem: t('service-offering.deleting-item'),
    updateItem: t('service-offering.updating-item'),
    emptyHeader: t('service-offering.empty-header'),
    emptySubHeader: t('service-offering.empty-subheader'),
    archiveStorageKey: archiveStorageKey
  };

  const renderContent = (status: APIStatus, globalTheme: ThemePrepared<any>) => {
    if (status.status === LOADING)
      return <Loader label={apiStatus.msg} className='h-screen' />

    if (status.status === ERROR) {
      return (status.errCode === 402) ?
        <UpgradeNeeded toolbarText={`${t('common.settings')} > ${t('service-offering.title')}`} breadcrumbs={navs} headerText={t('service-offering.payment.header')} subheaderText={t('service-offering.payment.subheader')} /> :
        <Communication fields={{ title: SOMETHING_WENT_WRONG, desc: CONTACT_ADMIN }} />;
    }

    const filteredList = offeringList.filter(x => search === "" || x.Name?.toLowerCase().includes(search.toLowerCase()) || x.Description?.toLowerCase().includes(search.toLowerCase()));

    return <DraggableList 
        columns={columns} 
        itemList={offeringList}
        setItemList={setOfferingList}
        search={search}
        setSearch={setSearch}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        reorderItemsCallback={reorderItems}
        filteredList={filteredList}
        isLicensedUser={isLicensedUser}
        queryId={queryId}
        setQueryId={setQueryId}
        setOpenEdit={setOpenEdit}
        navs={navs}
        setAPIStatus={setAPIStatus}
        fetchDataCallback={fetchData}
        deleteItemAPI={deleteOfferingApi}
        archiveItemAPI={archiveOfferingApi}
        textContents={textContents}
      />;
  };

  return (<ProviderConsumer render={(globalTheme) => (<>
    <Helmet>
      <title>{t('service-offering.title')}</title>
    </Helmet>
    <div className='hidden md:block'>{renderContent(apiStatus, globalTheme)}</div>
    <Offering open={openEdit} id={queryId} onCancel={() => { setOpenEdit(false); }} onSuccess={() => { setOpenEdit(false); fetchData(); }} />
    <MobileSettingsView />
  </>)} />);
}